import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FiSave, FiArrowLeft, FiUser, FiMapPin, FiPhone, FiMail } from 'react-icons/fi';
import clientsService from '../../../../services/clients.service';
import { useLocation } from 'react-router-dom';
import AppTour from '../../../../components/common/AppTour';

const ClientForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditing = !!id;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [runTour, setRunTour] = useState(false);

    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
        defaultValues: {
            nombre: '',
            rut: '',
            email: '',
            telefono: '',
            direccion: ''
        }
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        // Deshabilitado por orden del usuario para priorizar la vista de listas y evitar cortes.
        // if (params.get('tour') === 'clientes_nuevo') {
        //     setRunTour(true);
        // }

        if (isEditing) {
            loadClient();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, location.search]);

    const loadClient = async () => {
        try {
            setLoading(true);
            const client = await clientsService.getById(id);
            reset(client);
        } catch (err) {
            console.error('Error loading client:', err);
            setError('Error al cargar los datos del cliente');
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);
        setError('');

        // Formatear RUT antes de enviar (QUITAR PUNTOS para backend)
        if (data.rut) {
            data.rut = data.rut.replace(/\./g, '');
        }

        try {
            if (isEditing) {
                await clientsService.update(id, data);
            } else {
                await clientsService.create(data);
            }
            navigate('/app/cotizaplus/clientes');
        } catch (err) {
            console.error('Error saving client:', err);
            // Extraer mensaje de error del backend si existe
            let msg = 'Error al guardar el cliente.';

            if (err.response?.data) {
                const data = err.response.data;
                if (data.email) msg = 'El correo electrónico ya está registrado.';
                else if (data.rut) msg = `El RUT ya está registrado o es inválido (${data.rut}).`;
                else if (data.non_field_errors) {
                    const errorStr = String(data.non_field_errors);
                    if (errorStr.includes('conjunto único') || errorStr.includes('unique set')) {
                        msg = 'Ya existe un cliente con este RUT en su empresa.';
                    } else {
                        msg = errorStr;
                    }
                } else if (data.detail) {
                    msg = data.detail;
                }
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleRutBlur = (e) => {
        const value = e.target.value;
        if (value) {
            const formatted = clientsService.formatRut(value);
            setValue('rut', formatted);
        }
    };

    if (loading && isEditing) {
        return <div className="loading-overlay"><div className="spinner"></div></div>;
    }

    const clienteNuevoTourSteps = [
        {
            element: '#tour-cliente-form-header',
            intro: '<strong>Nuevo Cliente</strong>Aquí registrarás toda la información de contacto y facturación de tus clientes.',
            position: 'bottom',
        },
        {
            element: '#tour-cliente-form-nombre',
            intro: '<strong>Nombre o Razón Social</strong>Escribe el nombre completo o Razón Social tal y como debe aparecer en las facturas.',
            position: 'bottom',
        },
        {
            element: '#tour-cliente-form-rut',
            intro: '<strong>RUT</strong>El RUT es obligatorio; es el identificador único para que el sistema reconozca a tu cliente.',
            position: 'bottom',
        },
        {
            element: '#tour-cliente-form-email',
            intro: '<strong>Correo Electrónico</strong>Es el correo al que se enviarán automáticamente los presupuestos en PDF cuando decidas compartirlos.',
            position: 'top',
        },
        {
            element: '#tour-cliente-form-direccion',
            intro: '<strong>Dirección</strong>Agrega la dirección del cliente. Útil para coordinar despachos y dar una atención más personalizada.',
            position: 'top',
        }
    ];

    return (
        <div className="container-custom">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={clienteNuevoTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Continuar a Cotizaciones' }}
                    onTourComplete={() => {
                        window.location.href = '/app/cotizaplus/cotizaciones?tour=cotizaciones';
                    }}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '20px', borderBottom: 'none' }} id="tour-cliente-form-header">
                <div>
                    <h2>{isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
                    <p className="text-muted">Información de contacto y facturación</p>
                </div>
                <button className="btn-custom btn-secondary" onClick={() => navigate('/app/cotizaplus/clientes')}>
                    <FiArrowLeft size={20} /> Volver
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="card-custom" style={{ maxWidth: '800px', margin: '0 auto' }}>
                {error && <div className="alert-custom alert-danger" style={{ marginBottom: '20px' }}>{error}</div>}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>

                    <div className="form-group-custom" id="tour-cliente-form-nombre">
                        <label className="form-label-custom">
                            <FiUser style={{ marginRight: '8px' }} /> Nombre Completo / Razón Social *
                        </label>
                        <input
                            {...register('nombre', { required: 'El nombre es obligatorio' })}
                            className="form-control-custom"
                            placeholder="Ej: Empresa Ltda. o Juan Pérez"
                        />
                        {errors.nombre && <span className="form-error">{errors.nombre.message}</span>}
                    </div>

                    <div className="grid-1-to-2" style={{ gap: '20px' }}>
                        <div className="form-group-custom" id="tour-cliente-form-rut">
                            <label className="form-label-custom">RUT</label>
                            <input
                                {...register('rut', {
                                    required: 'El RUT es obligatorio',
                                    validate: value => clientsService.validateRut(value) || 'RUT inválido'
                                })}
                                className="form-control-custom"
                                placeholder="12345678-9"
                                onBlur={handleRutBlur}
                            />
                            {errors.rut && <span className="form-error">{errors.rut.message}</span>}
                        </div>

                        <div className="form-group-custom">
                            <label className="form-label-custom">
                                <FiPhone style={{ marginRight: '8px' }} /> Teléfono
                            </label>
                            <input
                                {...register('telefono')}
                                className="form-control-custom"
                                placeholder="+56 9 1234 5678"
                            />
                        </div>
                    </div>

                    <div className="form-group-custom" id="tour-cliente-form-email">
                        <label className="form-label-custom">
                            <FiMail style={{ marginRight: '8px' }} /> Correo Electrónico *
                        </label>
                        <input
                            type="email"
                            {...register('email', {
                                required: 'El email es obligatorio',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Email inválido'
                                }
                            })}
                            className="form-control-custom"
                            placeholder="contacto@cliente.com"
                        />
                        {errors.email && <span className="form-error">{errors.email.message}</span>}
                    </div>

                    <div className="form-group-custom" id="tour-cliente-form-direccion">
                        <label className="form-label-custom">
                            <FiMapPin style={{ marginRight: '8px' }} /> Dirección
                        </label>
                        <input
                            {...register('direccion')}
                            className="form-control-custom"
                            placeholder="Av. Principal 123, Ciudad"
                        />
                    </div>

                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid var(--gray-200)' }}>
                    <button
                        type="button"
                        className="btn-custom"
                        style={{ background: '#f8f9fa', color: '#333', border: '1px solid #ddd' }}
                        onClick={() => navigate('/app/cotizaplus/clientes')}
                    >
                        Cancelar
                    </button>
                    <button type="submit" className="btn-custom btn-primary" disabled={loading}>
                        <FiSave size={20} />
                        {loading ? 'Guardando...' : 'Guardar Cliente'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ClientForm;
